import { Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { ContractDetailPage } from '~/components/contractReview/detailPage/ContractDetailPage';
import { mapContract } from '~/components/contractReview/mapContract';
import {
  CONTRACT_FORM_REVIEW_RULES_TAB,
  editingContractIdKey,
  editingContractSectionKey,
} from '~/components/global/constants';
import { openInNewTab, RowPreviewPanel } from '~/components/rowPreview/RowPreviewPanel';
import type { RowPreview } from '~/components/rowPreview/useRowPreview';
import { useContractPreview } from '~/data/hooks/useContracts';
import { buildUrlForContract } from '~/utils/urlBuilder';

export function ContractPreviewContent({ contractId }: { contractId: string }) {
  const { contract: rawContract, error, loading } = useContractPreview(contractId);
  const contract = useMemo(
    () => (rawContract && rawContract.id === contractId ? mapContract(rawContract) : null),
    [rawContract, contractId],
  );
  const [, setSearchParams] = useSearchParams();

  const openEditForm = useCallback(
    (section?: string) => {
      setSearchParams((prev) => {
        prev.set(editingContractIdKey, contractId);
        if (section) {
          prev.set(editingContractSectionKey, section);
        } else {
          prev.delete(editingContractSectionKey);
        }
        return prev;
      });
    },
    [contractId, setSearchParams],
  );

  if (error && !contract) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
        This contract couldn’t be loaded.
      </Typography>
    );
  }
  return (
    <ContractDetailPage
      key={contractId}
      contract={contract}
      isLoading={loading || !contract}
      onBack={null}
      onEdit={() => openEditForm()}
      onAddRule={() => openEditForm(CONTRACT_FORM_REVIEW_RULES_TAB)}
      variant="preview"
    />
  );
}

export function ContractPreviewPanel({
  rowPreview,
  newTab = true,
}: {
  rowPreview: RowPreview;
  newTab?: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  return (
    <RowPreviewPanel
      rowPreview={rowPreview}
      ariaLabel="Contract preview"
      onOpenFullPage={(contractId) =>
        newTab
          ? openInNewTab(buildUrlForContract(contractId, searchParams))
          : void navigate({ pathname: `/contracts/${contractId}`, search: location.search })
      }
    >
      {(contractId) => <ContractPreviewContent contractId={contractId} />}
    </RowPreviewPanel>
  );
}
